import React, { useState, useEffect, useRef, createRef } from "react";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";
import styles from "./Slideshow.module.css";
import i18n from "../../Services/i18n";
import { MdInsertDriveFile } from "react-icons/md";

const Slideshow = ({ list1, list2 = [], onClose }) => {
  const [currentIndex1, setCurrentIndex1] = useState(0);
  const [currentIndex2, setCurrentIndex2] = useState(0);
  const [loading1, setLoading1] = useState(true);
  const [loading2, setLoading2] = useState(true);
  const [scale1, setScale1] = useState(1);
  const [scale2, setScale2] = useState(1);
  const [position1, setPosition1] = useState({ x: 0, y: 0 });
  const [position2, setPosition2] = useState({ x: 0, y: 0 });
  const [dragging1, setDragging1] = useState(false);
  const [dragging2, setDragging2] = useState(false);
  const [dragStart1, setDragStart1] = useState({ x: 0, y: 0 });
  const [dragStart2, setDragStart2] = useState({ x: 0, y: 0 });
  const [initialPos1, setInitialPos1] = useState({ x: 0, y: 0 });
  const [initialPos2, setInitialPos2] = useState({ x: 0, y: 0 });

  const thumbnailRefs1 = useRef([]);
  const thumbnailRefs2 = useRef([]);
  thumbnailRefs1.current = list1.map((_, i) => thumbnailRefs1.current[i] ?? createRef());
  thumbnailRefs2.current = list2.map((_, i) => thumbnailRefs2.current[i] ?? createRef());

  useEffect(() => {
    const item = list1[currentIndex1];

    setLoading1(item?.isImage && item?.url ? true : false);
    setScale1(1);
    setPosition1({ x: 0, y: 0 });
  }, [currentIndex1, list1]);

  useEffect(() => {
    if (list2.length > 0) {
      setLoading2(list2[currentIndex2]?.isImage && list2[currentIndex2]?.url ? true : false);
      setScale2(1);
      setPosition2({ x: 0, y: 0 });
    }
  }, [currentIndex2, list2]);

  const scrollToThumbnail = (refs, index) => {
    refs.current[index].current.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  };

  const handleThumbnailClick = (index, setIndex, refs) => {
    setIndex(index);
    refs.current[index].current.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  };

  const handleNext = (list, setIndex, currentIndex, refs) => {
    if (currentIndex < list.length - 1) {
      const newIndex = currentIndex + 1;
      setIndex(newIndex);
      scrollToThumbnail(refs, newIndex);
    }
  };

  const handlePrev = (setIndex, currentIndex, refs) => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setIndex(newIndex);
      scrollToThumbnail(refs, newIndex);
    }
  };

  const handleSyncPrev = (ref1, ref2) => {
    handlePrev(setCurrentIndex1, currentIndex1, ref1);
    if (list2.length > 0) {
      handlePrev(setCurrentIndex2, currentIndex2, ref2);
    }
  };

  const handleSyncNext = (ref1, ref2) => {
    handleNext(list1, setCurrentIndex1, currentIndex1, ref1);
    if (list2.length > 0) {
      handleNext(list2, setCurrentIndex2, currentIndex2, ref2);
    }
  };

  const zoomIn1 = () => {
    setScale1(scale1 + 0.2);
  };

  const zoomOut1 = () => {
    if (scale1 > 1) setScale1(scale1 - 0.2);
  };

  const zoomIn2 = () => {
    setScale2(scale2 + 0.2);
  };

  const zoomOut2 = () => {
    if (scale2 > 1) setScale2(scale2 - 0.2);
  };

  const startDrag1 = (e) => {
    setDragging1(true);
    setDragStart1({ x: e.clientX, y: e.clientY });
    setInitialPos1({ x: position1.x, y: position1.y });
  };

  const dragImage1 = (e) => {
    if (!dragging1) return;
    const deltaX = (e.clientX - dragStart1.x) / scale1;
    const deltaY = (e.clientY - dragStart1.y) / scale1;
    setPosition1({ x: initialPos1.x + deltaX, y: initialPos1.y + deltaY });
  };

  const stopDrag1 = () => {
    setDragging1(false);
  };

  const startDrag2 = (e) => {
    setDragging2(true);
    setDragStart2({ x: e.clientX, y: e.clientY });
    setInitialPos2({ x: position2.x, y: position2.y });
  };

  const dragImage2 = (e) => {
    if (!dragging2) return;
    const deltaX = (e.clientX - dragStart2.x) / scale2;
    const deltaY = (e.clientY - dragStart2.y) / scale2;
    setPosition2({ x: initialPos2.x + deltaX, y: initialPos2.y + deltaY });
  };

  const stopDrag2 = () => {
    setDragging2(false);
  };

  const containerStyle = {
    width: list2.length > 0 ? "50%" : "90%",
    height: "calc(100vh - 200px)",
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      dragImage1(e);
      dragImage2(e);
    };

    const handleMouseUp = () => {
      stopDrag1();
      stopDrag2();
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging1, dragging2]);

  const showImage = (list, currentIndex, scale, position, loading, setLoading, startDrag) => {
    if (!list || list.length === 0 || !list[currentIndex]) {
      return null;
    }

    const item = list[currentIndex];

    if (item.isImage && item.url) {
      return (
        <>
          <img
            key={item.url}
            src={item.url}
            alt={item.fileName}
            className={`${styles.zoomableImage} ${loading ? styles.hidden : ""}`}
            style={{
              transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
            }}
            onLoad={() => setLoading(false)}
            onMouseDown={startDrag}
            onDragStart={(e) => e.preventDefault()}
          />
          <p className={styles.fileName}>{item.fileName}</p>
        </>
      );
    } else {
      return (
        <>
          <div className={styles.placeholder}>
            <MdInsertDriveFile className={styles.fileIcon} />
          </div>
          <p className={styles.fileName}>{item.fileName}</p>
        </>
      );
    }
  };

  return (
    <div className={styles.backdrop}>
      <div className={styles.header}>
        <div className={styles.leftButtons}>
          <button
            className={styles.button}
            onClick={() => handlePrev(setCurrentIndex1, currentIndex1, thumbnailRefs1)}>
            {i18n.t("Generic.Previous")}
          </button>
          <button
            className={styles.button}
            onClick={() => handleNext(list1, setCurrentIndex1, currentIndex1, thumbnailRefs1)}>
            {i18n.t("Generic.Next")}
          </button>
        </div>

        {list2.length > 0 && (
          <div className={styles.centerButtons}>
            <button
              className={styles.button}
              onClick={() => {
                handleSyncPrev(thumbnailRefs1, thumbnailRefs2);
              }}>
              ⟵ Sync
            </button>
            <button
              className={styles.button}
              onClick={() => {
                handleSyncNext(thumbnailRefs1, thumbnailRefs2);
              }}>
              Sync ⟶
            </button>
          </div>
        )}

        {list2.length > 0 && (
          <div className={styles.rightButtons}>
            <button
              className={styles.button}
              onClick={() => handlePrev(setCurrentIndex2, currentIndex2, thumbnailRefs2)}>
              {i18n.t("Generic.Previous")}
            </button>
            <button
              className={styles.button}
              onClick={() => handleNext(list2, setCurrentIndex2, currentIndex2, thumbnailRefs2)}>
              {i18n.t("Generic.Next")}
            </button>
          </div>
        )}
        <div className={styles.rightButtons}>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>
      </div>

      <div className={styles.imageContainer}>
        <div className={styles.imageBox} style={containerStyle}>
          {loading1 &&
            (list1.length > 0 ? (
              <div className={styles.loading}>
                <LoadingSpinner />
              </div>
            ) : (
              <div className={styles.noItemFound}>
                <span>{i18n.t("Generic.List.NoItemFound")}</span>
              </div>
            ))}

          {showImage(list1, currentIndex1, scale1, position1, loading1, setLoading1, startDrag1)}

          <div className={styles.zoomControls}>
            <button className={styles.zoomButton} onClick={zoomIn1}>
              +
            </button>
            <button className={styles.zoomButton} onClick={zoomOut1}>
              -
            </button>
          </div>
        </div>

        {list2.length > 0 && <div className={styles.separator}></div>}

        {list2.length > 0 && (
          <div className={styles.imageBox} style={containerStyle}>
            {loading2 &&
              (list2.length > 0 ? (
                <div className={styles.loading}>
                  <LoadingSpinner />
                </div>
              ) : (
                <div className={styles.noItemFound}>
                  <span>{i18n.t("Generic.List.NoItemFound")}</span>
                </div>
              ))}

            {showImage(list2, currentIndex2, scale2, position2, loading2, setLoading2, startDrag2)}

            <div className={styles.zoomControls}>
              <button className={styles.zoomButton} onClick={zoomIn2}>
                +
              </button>
              <button className={styles.zoomButton} onClick={zoomOut2}>
                -
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={styles.thumbnailContainer}>
        <div className={styles.thumbnails} style={{ flex: 1 }}>
          {list1.map((item, index) => (
            <div
              key={index}
              ref={thumbnailRefs1.current[index]}
              className={`${styles.thumbnailCard} ${
                currentIndex1 === index ? styles.activeThumbnail : ""
              }`}
              onClick={() => handleThumbnailClick(index, setCurrentIndex1, thumbnailRefs1)}>
              {item.isImage ? (
                <img src={item.thumbnail} alt={item.fileName} className={styles.thumbnailImage} />
              ) : (
                <MdInsertDriveFile className={styles.fileIcon} />
              )}
            </div>
          ))}
        </div>

        {list2.length > 0 && <div className={styles.separator}></div>}

        {list2.length > 0 && (
          <div className={styles.thumbnails} style={{ flex: 1 }}>
            {list2.map((item, index) => (
              <div
                key={index}
                ref={thumbnailRefs2.current[index]}
                className={`${styles.thumbnailCard} ${
                  currentIndex2 === index ? styles.activeThumbnail : ""
                }`}
                onClick={() => handleThumbnailClick(index, setCurrentIndex2, thumbnailRefs2)}>
                {item.isImage ? (
                  <img src={item.thumbnail} alt={item.fileName} className={styles.thumbnailImage} />
                ) : (
                  <MdInsertDriveFile className={styles.fileIcon} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Slideshow;
